import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import BanqueEditModal from '../../components/BanqueEditModal';
import { archiveBank, searchBanks } from '../../api/banks';
import './BanquePage.css';

const BanquePage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  // "Supprimer" = archiver (jamais d'effacement : les fiches restent dans les
  // rapports) ; confirmation dans la ligne, pas de popup navigateur.
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [notice, setNotice] = useState('');

  const handleArchive = async (bank) => {
    setDeletingId(bank.id);
    setError('');
    try {
      await archiveBank(bank.id);
      setResults((prev) => prev.filter((b) => b.id !== bank.id));
      setNotice(`Banque ${bank.seq} - ${bank.name} supprimee. Ses fiches restent dans les rapports ; seul le superadmin peut la restaurer.`);
    } catch (err) {
      setError('Suppression impossible.');
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const [allBanks, setAllBanks] = useState([]);

  const runSearch = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const banks = await searchBanks('');
      const sorted = [...banks].sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
      setAllBanks(sorted);
      setResults(sorted);
    } catch (err) {
      setError('Recherche impossible — verifie que le serveur backend tourne.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    runSearch();
  }, [runSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const term = String(query ?? '').trim().toLowerCase();
      const filtered = term.length < 2 ? allBanks : allBanks.filter((bank) =>
        [
          bank.name,
          bank.seq,
          bank.username,
          bank.external_pos_id,
          bank.external_id,
          bank.pos_id,
          bank.group_name,
          bank.group?.name,
          bank.group?.label,
        ].some((value) => String(value ?? '').toLowerCase().includes(term))
      );
      setResults(filtered);
    }, 250);

    return () => clearTimeout(timer);
  }, [query, allBanks]);

  const handleSearch = (e) => {
    e.preventDefault();
    const term = String(query ?? '').trim().toLowerCase();

    const filtered = term.length < 2 ? allBanks : allBanks.filter((bank) =>
      [
        bank.name,
        bank.seq,
        bank.username,
        bank.group_name,
      ].some((value) => String(value ?? '').toLowerCase().includes(term))
    );

    setResults(filtered);
  };

  return (
    <Layout>
      <p className="bq-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <span className="bc-current">Parametres</span>
        {' >> '}
        <span className="bc-current">Banque</span>
      </p>

      <div className="bq-card">
        <div className="bq-card-header">
          <span className="bq-card-title">Bank</span>
        </div>
        <div className="bq-card-body">
          <button
            type="button"
            className="bq-new-btn"
            disabled
            title="Un bank se cree via la validation d'un device par le Grand Admin."
          >
            New
          </button>

          <form onSubmit={handleSearch} className="bq-search-row">
            <input
              className="bq-search-input"
              type="text"
              placeholder="ID POS externe, Username ou Groupe"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button type="submit" className="bq-search-btn" disabled={loading}>
              {loading ? 'Recherche...' : 'Search'}
            </button>
          </form>

          {error && <p className="bq-error">{error}</p>}
          {notice && <p className="bq-notice">{notice}</p>}

          <table className="bq-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Username</th>
            
                <th>Group</th>
                <th>Supervisor</th>
                <th>Address</th>
                <th>Telephone</th>
                <th>Comments</th>
                <th>Annulation</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {results.map((bank) => (
                <tr key={bank.id}>
                  <td>{bank.seq}</td>
                  <td>{bank.username || '—'}</td>
                  
                  <td>{bank.group_name || ''}</td>
                  <td>{(bank.supervisor_names || []).join(', ')}</td>
                  <td>{bank.address}</td>
                  <td>{bank.phone || '—'}</td>
                  <td>{bank.comment}</td>
                  <td>{bank.can_nullify ? 'Oui' : 'Non'}</td>
                  <td>
                    <div className="bq-actions">
                      <button
                        type="button"
                        className="bq-modify-btn"
                        onClick={() => setEditingId(bank.id)}
                      >
                        Modifier
                      </button>
                      {confirmDeleteId !== bank.id ? (
                        <button
                          type="button"
                          className="bq-delete-btn"
                          onClick={() => { setConfirmDeleteId(bank.id); setNotice(''); }}
                        >
                          Supprimer
                        </button>
                      ) : (
                        <>
                          <span className="bq-confirm-text">Supprimer ?</span>
                          <button
                            type="button"
                            className="bq-delete-btn"
                            disabled={deletingId === bank.id}
                            onClick={() => handleArchive(bank)}
                          >
                            {deletingId === bank.id ? '...' : 'Oui'}
                          </button>
                          <button type="button" className="bq-modify-btn" onClick={() => setConfirmDeleteId(null)}>
                            Non
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editingId && (
        <BanqueEditModal
          bankId={editingId}
          onClose={() => setEditingId(null)}
          onSaved={(updated) => {
            setResults((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
          }}
        />
      )}
    </Layout>
  );
};

export default BanquePage;
